import React, { useEffect, useState } from "react";
import { Download, Eye, Mail } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ORDER } from "@/constants/testIds";
import SendVendorPacketDialog from "@/components/orders/SendVendorPacketDialog";

/**
 * VendorPanel — pro-shop only.
 * Edits confirmed vendor + PO #.
 */
export default function VendorPanel({ order, onUpdated }) {
  const [form, setForm] = useState({
    confirmed_vendor: "",
    confirmed_vendor_id: "",
    vendor_po: "",
  });
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  // iter69 · Vendor picker — approved vendors the club can pick from so we
  // stamp a stable `confirmed_vendor_id` in addition to the display string.
  const [vendorOptions, setVendorOptions] = useState([]);
  const [showSendDialog, setShowSendDialog] = useState(false);

  useEffect(() => {
    setForm({
      confirmed_vendor: order.confirmed_vendor || "",
      confirmed_vendor_id: order.confirmed_vendor_id || "",
      vendor_po: order.vendor_po || "",
    });
  }, [order]);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/vendors/picker")
      .then(({ data }) => { if (!cancelled) setVendorOptions(data.vendors || []); })
      .catch(() => { /* silent — vendor picker is a progressive enhancement */ });
    return () => { cancelled = true; };
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // When the free-text field matches an approved vendor by name, resolve to
  // that vendor's stable id. Typing a new name clears the id (free-text).
  const onConfirmedVendorChange = (e) => {
    const name = e.target.value;
    const match = vendorOptions.find(
      (v) => (v.company_name || "").toLowerCase() === name.trim().toLowerCase(),
    );
    setForm((f) => ({
      ...f,
      confirmed_vendor: name,
      confirmed_vendor_id: match ? match.vendor_id : "",
    }));
  };

  const handleSave = async () => {
    setError("");
    setSaving(true);
    setSaved(false);
    try {
      const { data } = await api.patch(`/orders/${order.order_id}/vendor`, {
        ...form,
        notify_member: notify,
      });
      onUpdated?.(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [previewingPdf, setPreviewingPdf] = useState(false);
  const [includeMemberNote, setIncludeMemberNote] = useState(true);

  // iter67 §11 · Open the packet PDF in a NEW TAB without stamping the
  // download-audit timeline. We fetch the blob via axios (cookies travel)
  // then window.open() the object URL — this works cross-origin where a
  // plain <a href> would fail on production.
  const previewVendorPacket = async () => {
    setError("");
    setPreviewingPdf(true);
    try {
      const resp = await api.get(`/admin/orders/${order.order_id}/vendor-packet.pdf`, {
        params: {
          include_member_note: includeMemberNote ? "true" : "false",
          preview: "true",
        },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Revoke a bit later — the new tab needs the URL alive to render.
      setTimeout(() => URL.revokeObjectURL(url), 20000);
    } catch (err) {
      let detail = err.message;
      if (err.response?.data instanceof Blob) {
        try {
          const txt = await err.response.data.text();
          detail = (JSON.parse(txt).detail) || txt || detail;
        } catch (_e) { /* keep original */ }
      } else {
        detail = formatApiErrorDetail(err.response?.data?.detail) || detail;
      }
      setError(`Vendor packet preview failed: ${detail}`);
    } finally {
      setPreviewingPdf(false);
    }
  };

  const downloadVendorPacket = async () => {
    // Fetch via axios so cookies are sent with-credentials in production
    // (a plain <a target=_blank href=...> won't carry cross-origin cookies on
    // mycrestclub.com → API host, which is why the direct link returned 401).
    setError("");
    setDownloadingPdf(true);
    try {
      const resp = await api.get(`/admin/orders/${order.order_id}/vendor-packet.pdf`, {
        params: { include_member_note: includeMemberNote ? "true" : "false" },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vendor-packet-${order.order_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after the click so the download has time to register.
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (err) {
      // If the server returned JSON error (it'll be in a blob), read it back as text.
      let detail = err.message;
      if (err.response?.data instanceof Blob) {
        try {
          const txt = await err.response.data.text();
          detail = (JSON.parse(txt).detail) || txt || detail;
        } catch (_) { /* fall through with original message */ }
      } else {
        detail = formatApiErrorDetail(err.response?.data?.detail) || detail;
      }
      setError(`Vendor packet download failed: ${detail}`);
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="surface-card p-8">
      <div className="overline">Vendor &amp; Fulfillment</div>
      <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Place with Vendor</h2>
      <p className="text-sm text-[#7A7A7A] mt-1">
        Update once the order has been placed. Kept private to the pro shop —
        members only see the status of their order.
      </p>

      {error && (
        <div className="mt-4 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
        <div className="md:col-span-2">
          <label className="label-elegant block mb-2">
            Confirmed Vendor
            {form.confirmed_vendor_id && (
              <span className="ml-2 inline-block px-2 py-0.5 text-[9px] uppercase tracking-widest bg-[#E9F0EB] text-[#1A422D] border border-[#1A422D]" data-testid="vendor-picker-resolved">
                Approved · id linked
              </span>
            )}
          </label>
          <input
            type="text"
            list="vendor-picker-options"
            value={form.confirmed_vendor}
            onChange={onConfirmedVendorChange}
            data-testid={ORDER.vendorConfirmed}
            className="input-field"
            placeholder="Pick approved vendor or type a name"
          />
          <datalist id="vendor-picker-options" data-testid="vendor-picker-list">
            {vendorOptions.map((v) => (
              <option key={v.vendor_id} value={v.company_name}>
                {v.primary_category ? `${v.company_name} — ${v.primary_category}` : v.company_name}
              </option>
            ))}
          </datalist>
          <div className="mt-1 text-[10px] text-[#7A7A7A]">
            {vendorOptions.length > 0
              ? `${vendorOptions.length} approved vendor${vendorOptions.length === 1 ? "" : "s"} available — pick one to attach a stable ID.`
              : "Free-text vendor name (no approved vendors on file yet)."}
          </div>
        </div>
        <div>
          <label className="label-elegant block mb-2">Vendor Order / PO #</label>
          <input
            type="text"
            value={form.vendor_po}
            onChange={update("vendor_po")}
            data-testid={ORDER.vendorPo}
            className="input-field"
            placeholder="PM-29483"
          />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
        {saved && <span className="text-sm text-[#1A422D]">Saved.</span>}
        <label
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#4A4A4A] cursor-pointer select-none mr-auto"
          title={
            order?.notes
              ? "Toggles the 'Notes from the Member' section in the generated vendor PDF."
              : "This order has no member note — the toggle has no effect."
          }
        >
          <input
            type="checkbox"
            checked={includeMemberNote}
            onChange={(e) => setIncludeMemberNote(e.target.checked)}
            data-testid="vendor-packet-include-note"
            className="accent-[#1A422D] w-4 h-4"
            disabled={!order?.notes}
          />
          <span className={!order?.notes ? "opacity-50" : ""}>Include member note on vendor packet</span>
        </label>
        <a
          href="#preview-vendor-packet"
          onClick={(e) => { e.preventDefault(); previewVendorPacket(); }}
          aria-disabled={previewingPdf}
          data-testid="vendor-packet-preview"
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold border border-[#B59A5A] text-[#8a6d2e] hover:bg-[#FFFCEF] transition-colors ${previewingPdf ? "opacity-60 cursor-progress" : ""}`}
        >
          <Eye size={14} /> {previewingPdf ? "Opening…" : "Preview Vendor Packet"}
        </a>
        <a
          href="#download-vendor-packet"
          onClick={(e) => { e.preventDefault(); downloadVendorPacket(); }}
          aria-disabled={downloadingPdf}
          data-testid="vendor-packet-download"
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold border border-[#1A422D] text-[#1A422D] hover:bg-[#E9F0EB] transition-colors ${downloadingPdf ? "opacity-60 cursor-progress" : ""}`}
        >
          <Download size={14} /> {downloadingPdf ? "Preparing…" : "Download Vendor Packet"}
        </a>
        <button
          type="button"
          onClick={() => setShowSendDialog(true)}
          data-testid="vendor-packet-send"
          className="inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold bg-[#1A422D] text-[#F9F8F4] hover:bg-[#0f1a14] transition-colors"
        >
          <Mail size={14} /> Send Vendor Packet
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          data-testid={ORDER.vendorSave}
          className="btn-primary"
        >
          {saving ? "Saving…" : "Save Vendor Info"}
        </button>
      </div>
      <SendVendorPacketDialog
        open={showSendDialog}
        onClose={() => setShowSendDialog(false)}
        orderId={order.order_id}
        orderDisplayId={order.order_number || order.order_id}
        onSent={async () => {
          // Re-fetch the order so the activity_log entry + timeline update
          // from POST /admin/orders/:id/send-vendor-packet are visible to
          // the staff without a full page reload. IMPORTANT: `onUpdated`
          // is `setOrder`, so passing no argument would set order=undefined
          // and lock the page on its "Loading…" state — that was the
          // "clicked Send Vendor Packet, screen stuck loading" bug.
          try {
            const { data } = await api.get(`/orders/${order.order_id}`);
            onUpdated?.(data);
          } catch {
            /* silent — the send already succeeded server-side */
          }
        }}
      />
    </div>
  );
}
